import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../api/client';
import { FechamentoCaixaItemDto, FechamentoCaixaResponse } from '../../types';

const FechamentoCaixa: React.FC = () => {
  usePageTitle('Fechamento de Caixa');
  const hoje = new Date().toISOString().split('T')[0];
  const [dataSelecionada, setDataSelecionada] = useState(hoje);
  const [dataConsulta, setDataConsulta] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['fechamento-caixa', dataConsulta],
    queryFn: async () => {
      const response = await api.get<FechamentoCaixaResponse>('/fechamento-caixa', {
        params: { data: dataConsulta },
      });
      return response.data;
    },
    enabled: !!dataConsulta,
  });

  const agruparItensCaixa = (itens: FechamentoCaixaItemDto[]) => {
    const mapa = new Map<number, FechamentoCaixaItemDto>();
    for (const item of itens) {
      if (mapa.has(item.produtoId)) {
        const g = mapa.get(item.produtoId)!;
        g.quantidade += item.quantidade;
        g.valorVenda += item.valorVenda;
        g.valorCusto += item.valorCusto;
      } else {
        mapa.set(item.produtoId, { ...item });
      }
    }
    return Array.from(mapa.values()).sort((a, b) => a.nomeProduto.localeCompare(b.nomeProduto, 'pt-BR'));
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatDate = (isoDate: string) =>
    new Date(isoDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const handleCalcular = () => {
    setDataConsulta(dataSelecionada);
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Fechamento de Caixa</h1>

      {/* Seleção de data */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
            <input
              type="date"
              value={dataSelecionada}
              max={hoje}
              onChange={(e) => setDataSelecionada(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleCalcular}
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {isLoading ? 'Calculando...' : 'Calcular'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
          Erro ao carregar o fechamento de caixa. Tente novamente.
        </div>
      )}

      {data && (
        <>
          {/* Resumo financeiro */}
          <div className="grid grid-cols-2 gap-4 mb-6 lg:grid-cols-4">
            <div className="bg-white rounded-lg shadow p-5">
              <p className="text-sm text-gray-500 mb-1">Total de Vendas</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(data.valorTotalPedidos)}
              </p>
              <p className="text-xs text-gray-400 mt-1">{data.pedidos.length} pedido(s)</p>
            </div>
            <div className="bg-white rounded-lg shadow p-5">
              <p className="text-sm text-gray-500 mb-1">Custo dos Produtos</p>
              <p className="text-2xl font-bold text-orange-500">
                {formatCurrency(data.custoProdutosTotal)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-5">
              <p className="text-sm text-gray-500 mb-1">Custo Fixo (18%)</p>
              <p className="text-2xl font-bold text-yellow-600">
                {formatCurrency(data.custoFixo)}
              </p>
            </div>
            <div className={`rounded-lg shadow p-5 ${data.lucroLiquido >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <p className="text-sm text-gray-500 mb-1">Lucro Líquido</p>
              <p className={`text-2xl font-bold ${data.lucroLiquido >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(data.lucroLiquido)}
              </p>
            </div>
          </div>

          {data.pedidos.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-500 text-center py-4">
                Nenhum pedido pago encontrado para esta data.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Pedidos</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {data.pedidos.map((pedido) => (
                  <div key={pedido.id} className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-700">
                          Pedido #{pedido.id}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatDate(pedido.dataHora)}
                        </span>
                        {pedido.numeroMesa && (
                          <span className="text-sm text-gray-600">— Mesa {pedido.numeroMesa}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <span className="text-gray-500">
                          Custo: <span className="font-medium text-orange-500">{formatCurrency(pedido.custoTotal)}</span>
                        </span>
                        <span className="font-semibold text-gray-900">
                          Venda: {formatCurrency(pedido.valorTotal)}
                        </span>
                      </div>
                    </div>

                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                          <th className="pb-1 font-medium">Produto</th>
                          <th className="pb-1 font-medium text-center">Qtd</th>
                          <th className="pb-1 font-medium text-right">Preço Unit.</th>
                          <th className="pb-1 font-medium text-right">Custo Unit.</th>
                          <th className="pb-1 font-medium text-right">Venda</th>
                          <th className="pb-1 font-medium text-right">Custo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agruparItensCaixa(pedido.itens).map((item) => (
                          <tr key={item.produtoId} className="border-b border-gray-50">
                            <td className="py-1 text-gray-700">{item.nomeProduto}</td>
                            <td className="py-1 text-center text-gray-600">{item.quantidade}</td>
                            <td className="py-1 text-right text-gray-600">{formatCurrency(item.precoUnitario)}</td>
                            <td className="py-1 text-right text-orange-500">{formatCurrency(item.custoUnitario)}</td>
                            <td className="py-1 text-right font-medium text-gray-800">{formatCurrency(item.valorVenda)}</td>
                            <td className="py-1 text-right text-orange-500">{formatCurrency(item.valorCusto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>

              {/* Rodapé com totais */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex justify-end gap-8 text-sm font-medium">
                  <span className="text-gray-600">
                    Total de vendas: <span className="text-blue-600">{formatCurrency(data.valorTotalPedidos)}</span>
                  </span>
                  <span className="text-gray-600">
                    Custo produtos: <span className="text-orange-500">({formatCurrency(data.custoProdutosTotal)})</span>
                  </span>
                  <span className="text-gray-600">
                    Custo fixo: <span className="text-yellow-600">({formatCurrency(data.custoFixo)})</span>
                  </span>
                  <span className={`font-bold ${data.lucroLiquido >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    Lucro líquido: {formatCurrency(data.lucroLiquido)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FechamentoCaixa;
